import * as React from 'react';
import { Table, PopupAction } from '@teamsnap/teamsnap-ui';
import { useQuery, useMutation } from '@apollo/client';
import {
  QUERY as QueryStaff,
  GqlStaff,
} from '../../core/gql/queries/show-all-staff';
import * as GqlPrograms from '../../core/gql/queries/programs';
import * as DeleteStaff from '../../core/gql/mutations/delete-staff';
import * as ResendInvitation from '../../core/gql/mutations/resend-invitation';
import { reactiveFeedbackMessage } from '../../core/apollo-cache';
import { AuthContext } from 'auth/dist/contexts';
import { hasPermissionForOrg, Permission } from '../../core/permissions';
import staffRow from './staff-row';
import { isOwner } from '../../core/utils';
import CreateStaffSidebar from './create-staff';
import useChurnZero from 'components/common/useChurnZero';

export interface Props {
  organizationId: number;
  className?: string;
  reloadStaff?: boolean;
}

const StaffList: React.FunctionComponent<Props> = ({
  organizationId,
  className,
  reloadStaff,
}) => {
  const [editStaff, setEditStaff] = React.useState();

  const { data, loading, refetch } = useQuery(QueryStaff, {
    variables: { organizationId: organizationId },
    errorPolicy: 'all',
  });
  const staff = data?.showOrganization.staff || [];
  const queryPrograms = useQuery(GqlPrograms.QUERY, {
    variables: { organizationId: organizationId },
  });
  const [deleteStaff] = useMutation(DeleteStaff.MUTATION);
  const [resendInvitation] = useMutation(ResendInvitation.MUTATION);
  const context = React.useContext(AuthContext);
  const canUserEditStaff = hasPermissionForOrg(
    context.user,
    Permission.StaffUpdate,
    organizationId
  );
  const canUserDeleteStaff = hasPermissionForOrg(
    context.user,
    Permission.StaffDelete,
    organizationId
  );
  const { setChurnZeroAttribute } = useChurnZero({
    organizationId: organizationId,
  });

  React.useEffect(() => {
    if (reloadStaff) {
      refetch();
    }
  }, [reloadStaff, refetch]);

  const closeSidebar = (reload: boolean) => {
    document.body.classList.remove('scroll-lock');
    setEditStaff(null);
    if (reload) {
      refetch();
    }
  };

  const renderTable = (
    staff: GqlStaff[],
    loading: boolean,
    organizationId: number
  ) => {
    const sortByKeys = (element1, element2) => {
      const aString = element1.key.toLowerCase();
      const bString = element2.key.toLowerCase();

      if (aString === bString) {
        return 0;
      } else {
        return aString < bString ? -1 : 1;
      }
    };
    return (
      <Table
        isLoading={loading}
        columns={[
          {
            name: 'col1',
            label: 'Name',
            isSortable: true,
            sortFn: sortByKeys,
            align: 'left',
            mods: 'u-size5of24',
          },
          {
            name: 'col2',
            label: 'Role',
            isSortable: true,
            sortFn: sortByKeys,
            align: 'left',
            mods: 'u-size7of24',
          },
          {
            name: 'col3',
            label: 'Contact',
            isSortable: true,
            sortFn: sortByKeys,
            align: 'left',
            mods: 'u-size7of24',
          },
          {
            name: 'col4',
            label: 'Invitation',
            isSortable: true,
            sortFn: sortByKeys,
            align: 'left',
            mods: 'u-size3of24',
          },
          {
            name: 'col5',
            label: 'Actions',
            isSortable: false,
            align: 'center',
            mods: 'u-size2of24',
          },
        ]}
        rows={createTableRowData(staff, organizationId)}
      />
    );
  };

  const renderActionColumn = (staff, organizationId) => {
    const deleteInviteCallback = async () => {
      const data = { organizationId: organizationId, staffId: staff.id };
      try {
        await deleteStaff({
          variables: data as DeleteStaff.Input,
          refetchQueries: () => [
            {
              query: QueryStaff,
              variables: { organizationId: organizationId },
            },
          ],
        });
        // something went right!
        reactiveFeedbackMessage({
          message: `Successfully removed ${staff.firstName} ${staff.lastName} as an admin.`,
          isError: false,
          autoclose: true,
        });

        setChurnZeroAttribute(
          staff.firstName,
          staff.lastName,
          staff.emailAddress,
          'Removed from Org',
        );
      } catch (e) {
        // something went wrong!
        reactiveFeedbackMessage({
          message: `We couldn't remove ${staff.firstName} ${staff.lastName} as an admin. Check to make sure they are not a league owner in one of your program seasons. You can contact TeamSnap support to change the owner of any program.`,
          isError: false,
          autoclose: true,
        });
      }
    };

    const resendInviteCallback = async () => {
      const data = { organizationId: organizationId, invitationId: staff.id };
      try {
        await resendInvitation({
          variables: data,
          refetchQueries: () => [
            {
              query: QueryStaff,
              variables: { organizationId: organizationId },
            },
          ],
        });
        // something went right!
        reactiveFeedbackMessage({
          message: `Successfully resent invitation to ${staff.firstName} ${staff.lastName}.`,
          isError: false,
          autoclose: true,
        });
      } catch (e) {
        // something went wrong!
        reactiveFeedbackMessage({
          message: `We couldn't resent invitation to ${staff.firstName} ${staff.lastName}.`,
          isError: false,
          autoclose: true,
        });
      }
    };

    const actions: any[] =
      canUserEditStaff &&
      staff.invitationStatus.toLowerCase() === 'accepted' &&
      !isOwner(staff)
        ? [
            {
              text: 'Edit',
              callback: () => {
                setEditStaff(staff);
              },
            },
          ]
        : [];

    if (
      canUserEditStaff &&
      staff.invitationStatus.toLowerCase() === 'pending'
    ) {
      actions.push({
        text: 'Resend Invite',
        callback: resendInviteCallback,
        requiresConfirmation: true,
        confirmationText: 'Are you sure you want to resend invitation?',
      });
    }

    if (canUserDeleteStaff && !isOwner(staff)) {
      actions.push({
        text: 'Delete',
        callback: deleteInviteCallback,
        requiresConfirmation: true,
        confirmationText:
          'This user will no longer have access to organization administration and program season commissioner functions in TeamSnap. It will not remove them as a coach, player or member of your organization.',
      });
    }

    if (actions.length > 0) {
      return (
        <PopupAction
          direction={['down']}
          text="..."
          actions={actions}
          popupStyle={{ width: '150px' }}
        />
      );
    }

    return <></>;
  };

  const createTableRowData = (
    staffList: GqlStaff[],
    _organizationId: number
  ): Object[] =>
    staffList.map((staff, pidx) =>
      staffRow(
        staff,
        organizationId,
        renderActionColumn(staff, organizationId),
        queryPrograms,
        pidx
      )
    );

  return (
    <div className={'container staff-list ' + className}>
      {editStaff && (
        <CreateStaffSidebar
          open
          orgId={organizationId}
          editStaff={editStaff}
          close={closeSidebar}
        />
      )}
      <div className="page-content-body">
        {renderTable(staff, loading, organizationId)}
      </div>
    </div>
  );
};

export default StaffList;
